import React from 'react';
import { media, style } from 'typestyle';

import { CloseButtonWidget } from '@tableau/widgets';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { hideMetricsBanner } from '../../actions/UpdateMetricsActions';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { MapDispatchToProps } from '../../../app/utils/ConnectUtils';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { MobileAppService } from '../../../mobile/services/MobileAppService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { BreakPoints } from './MetricsStyles';
import { MetricBlue } from '@tableau/metrics-engine';

export interface MetricsBannerOwnProps {
}

export interface MetricsBannerAngularProps {
  readonly sessionManagementService: SessionManagementService;
}

export interface MetricsBannerDispatchProps {
  readonly hideMetricsBanner: () => void;
}

export interface MetricsBannerState {
  readonly shouldShowBanner: boolean;
}

export type MetricsBannerPropsWithoutAngular = MetricsBannerOwnProps & MetricsBannerDispatchProps;
export type MetricsBannerProps = MetricsBannerPropsWithoutAngular & MetricsBannerAngularProps;

export class MetricsBanner extends React.PureComponent<MetricsBannerProps, MetricsBannerState> {
  public constructor(props: MetricsBannerProps) {
    super(props);
    this.state = {
      shouldShowBanner: this.props.sessionManagementService.isMetricsBannerEnabledForUser(),
    };
  }

  public render() {
    if (!this.state.shouldShowBanner) {
      return null;
    }
    return (
      <div className={metricsBannerContainerStyleClass}>
        <div className={metricsBannerTextContainerStyleClass}>
          <div className={metricsBannerTextStyleClass}>
            {Messages.metricsBannerText()}
          </div>
          <div className={metricsBannerLearnMoreContainerStyleClass}>
            {this.renderLink()}
          </div>
        </div>
        {this.closeButton()}
      </div>
    );
  }

  private renderLink(): JSX.Element {
    if (MobileAppService.isUserProbablyOnMobile()) {
      return this.openTableauMobileLink();
    } else {
      return this.learnMoreLink();
    }
  }

  private learnMoreLink(): JSX.Element {
    return <a href={'https://www.tableau.com/metrics'} style={metricsBannerLinkStyle} target={'_blank'} rel={'noopener noreferrer'} data-tb-test-id={'metrics-banner-learn-more'}>{Messages.metricsBannerLearnMore()}</a>;
  }

  private openTableauMobileLink(): JSX.Element {
    const onClickHandler = () => { MobileAppService.onPressAppBanner(); };
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return <a onClick={onClickHandler} style={metricsBannerLinkStyle} target={'_blank'} data-tb-test-id={'metrics-banner-open-tableau-mobile'}>{Messages.metricsBannerOpenTableauMobile()}</a>;
  }

  private closeButton(): JSX.Element {
    const buttonStyleClass: string = style(
      {
        cursor: 'pointer',
        position: 'absolute',
        top: '0px',
        right: '0px'
      },
      media({ minWidth: 0, maxWidth: BreakPoints.LARGE }, { height: '40px !important', width: '40px !important' }),
      media({ minWidth: BreakPoints.LARGE + 1 }, { height: '56px !important', width: '56px !important' })
    );
    const testId = 'metrics-banner-dismiss';
    const onDismiss = () => {
      this.setState({ shouldShowBanner: false });
      this.props.hideMetricsBanner();
    };
    const buttonWidgetProps = {
      handleClick: onDismiss,
      testId: testId,
      useDarkStyling: true,
      className: buttonStyleClass
    };

    return (
      <CloseButtonWidget {...buttonWidgetProps}/>
    );
  }
}

export namespace MetricsBannerAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const SessionManagementService = getSessionManagementServiceInstance();

    return {
      sessionManagementService: SessionManagementService,
    };
  }

  function selectPropsFromAngularServices(angularServices: MetricsBannerAngularProps) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MetricsBannerPropsWithoutAngular, MetricsBannerAngularProps, MetricsBannerAngularProps>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const MetricsBannerAngular: React.ComponentType<MetricsBannerPropsWithoutAngular> = MetricsBannerAngularProvider.HOC(MetricsBanner);

const mapDispatchToProps: MapDispatchToProps<MetricsBannerDispatchProps> = (dispatch) => bindActionCreators({
   hideMetricsBanner: hideMetricsBanner,
}, dispatch);

export const ConnectedMetricsBanner: React.ComponentClass<MetricsBannerOwnProps>
  = connect(undefined, mapDispatchToProps)(MetricsBannerAngular);

const metricsBannerContainerStyleClass: string = style(
  {
    backgroundColor: MetricBlue,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative'
  },
  media({ minWidth: 0, maxWidth: BreakPoints.SMALL }, { minHeight: '138px' }),
  media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.LARGE }, { minHeight: '128px' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { minHeight: '56px' })
);
const metricsBannerTextContainerStyleClass: string = style(
  {
    minWidth: 'fit-content',
    minHeight: 'fit-content',
    display: 'flex'
  },
);
const metricsBannerTextStyleClass: string = style(
  {
    color: 'white',
    fontSize: '18px',
  },
  media({ minWidth: 0, maxWidth: BreakPoints.SMALL }, { fontSize: '16px', lineHeight: '24px', marginLeft: '50px', marginRight: '50px' }),
  media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.LARGE }, { marginLeft: '50px', marginRight: '50px' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { marginRight: '24px' })
);
const metricsBannerLearnMoreContainerStyleClass: string = style(
  {
    minWidth: 'fit-content',
  },
  media({ minWidth: 0, maxWidth: BreakPoints.LARGE },
    {
      position: 'absolute',
      bottom: '16px',
      right: '24px'
    }
  )
);
const metricsBannerLinkStyle: React.CSSProperties = {
  fontWeight: 'bold',
  color: 'white',
  fontSize: '18px'
};
